import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Loader2, Sparkles, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

export default function ExamCenterPage() {
  const nav = useNavigate();
  const [exams, setExams] = useState([]);
  const [trend, setTrend] = useState([]);
  const [creating, setCreating] = useState(null);

  const load = () => Promise.all([
    api.get("/exams").then((r) => setExams(r.data)),
    api.get("/exams/trend").then((r) => setTrend(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async (type, subjects, count) => {
    setCreating(type);
    try {
      const r = await api.post("/exams/generate", { type, subjects, count });
      nav(`/denemeler/${r.data.id}`);
    } catch {
      toast.error("Deneme oluşturulamadı");
    } finally {
      setCreating(null);
    }
  };

  return (
    <div className="space-y-5 ela-fade-in" data-testid="exam-center-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Deneme Merkezi</h1>
        <p className="text-sm text-slate-500 mt-1">LGS tarzı yeni nesil sorularla kendini sına</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6 rounded-2xl border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">Haftalık</Badge>
          </div>
          <h3 className="font-heading text-xl font-bold text-slate-900">Mini Deneme</h3>
          <p className="text-sm text-slate-500 mt-1">10 soru · Matematik + Türkçe</p>
          <Button
            onClick={() => create("weekly", ["matematik", "turkce"], 10)}
            disabled={creating === "weekly"}
            data-testid="create-weekly-exam-btn"
            className="mt-4 bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl w-full">
            {creating === "weekly" ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Hazırlanıyor</> : "Mini Denemeyi Başlat"}
          </Button>
        </Card>

        <Card className="p-6 rounded-2xl border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="bg-rose-50 text-rose-700">Aylık · LGS</Badge>
          </div>
          <h3 className="font-heading text-xl font-bold text-slate-900">LGS Denemesi</h3>
          <p className="text-sm text-slate-500 mt-1">20 soru · Matematik + Türkçe karma</p>
          <Button
            onClick={() => create("lgs", ["matematik", "turkce"], 20)}
            disabled={creating === "lgs"}
            data-testid="create-lgs-exam-btn"
            className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-xl w-full">
            {creating === "lgs" ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Hazırlanıyor</> :
              <><Sparkles className="w-4 h-4 mr-2" />LGS Denemesini Başlat</>}
          </Button>
        </Card>
      </div>

      <Card className="rounded-2xl border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-slate-900">Geçmiş Denemeler</h3>
        </div>
        {exams.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Henüz deneme çözmedin.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {exams.map((e) => (
              <button key={e.id} onClick={() => nav(`/denemeler/${e.id}`)}
                data-testid={`exam-history-${e.id}`}
                className="w-full p-4 hover:bg-slate-50 flex items-center justify-between text-left">
                <div>
                  <div className="text-sm font-medium text-slate-900">
                    {e.type === "lgs" ? "LGS Denemesi" : "Mini Deneme"} ·{" "}
                    <span className="text-slate-500 font-normal">{new Date(e.created_at).toLocaleString("tr-TR")}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.subjects?.join(", ")}
                  </div>
                </div>
                <div className="text-right">
                  {e.submitted ? (
                    <>
                      <div className="font-display text-xl font-bold text-slate-900">{e.score}</div>
                      <div className="text-[10px] text-slate-500">{e.correct}/{e.total}</div>
                    </>
                  ) : (
                    <Badge variant="outline">Devam</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {trend.length >= 2 && (
        <Card className="p-5 rounded-2xl border-slate-200" data-testid="exam-trend-chart">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#FF6B6B]" />
            <h3 className="font-heading font-semibold text-slate-900">Deneme Trend Grafiği</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">Son {trend.length} denemenin puan değişimi</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.map((t, i) => ({
                ad: `#${i + 1}`,
                tarih: t.date,
                Puan: t.score,
                tip: t.type,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="ad" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Puan" stroke="#FF6B6B" strokeWidth={2.5} dot={{ r: 4, fill: "#FF6B6B" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
